import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV, PHONE, TEL } from "@/data/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const solid = scrolled || open;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        solid ? "bg-[#14263D]/95 py-3 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)] backdrop-blur-md" : "bg-gradient-to-b from-[#0e1b2c]/80 to-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-5 md:px-10">
        <Link to="/" data-testid="header-logo-link" aria-label="First Due Home Watch & Concierge home">
          <Logo light />
        </Link>
        <nav className="hidden items-center gap-6 xl:flex" aria-label="Main">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
              className={({ isActive }) => `link-underline pb-0.5 text-[0.82rem] font-medium text-white/90 hover:text-white ${isActive ? "active text-white" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={TEL}
            data-testid="header-call-now-btn"
            className="btn-sweep on-dark hidden items-center gap-2 rounded-full bg-[#A52A32] px-5 py-2.5 text-sm font-semibold text-white sm:inline-flex"
          >
            <Phone className="h-4 w-4" /> Call Now <span className="hidden font-mono text-xs lg:inline">{PHONE}</span>
          </a>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            aria-label="Mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden xl:hidden"
          >
            <div className="mx-auto max-w-[1400px] px-5 pb-6 pt-4 md:px-10">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ x: -16, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
                  <NavLink
                    to={n.to}
                    end
                    data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                    className={({ isActive }) => `flex items-baseline justify-between border-b border-white/10 py-3 font-display text-2xl text-white ${isActive ? "text-[#f3b9bd]" : ""}`}
                  >
                    {n.label}
                    <span className="font-mono text-xs text-white/60">0{i + 1}</span>
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
